import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Overlays (P0-9 deliverable 10): Popover and HoverCard on Base UI
 * primitives, and the two peeks; the kit menu is `menu.tsx` (UI r2). Every
 * overlay renders inside the token root (`PortalContainer`), registers with the layer stack so Escape closes only
 * the innermost one, and gives focus back to its trigger when it closes.
 */
import { Popover as BasePopover } from '@base-ui/react/popover';
import { PreviewCard } from '@base-ui/react/preview-card';
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { hasRestoredFocus, useFocusTrap, useLayer, usePortalContainer, useReturnFocus, } from './infra.js';
import { IconButton } from './primitives.js';
export function Popover({ trigger, triggerLabel, title, children, triggerClassName, open: openProp, onOpenChange, triggerTestId, disabled, }) {
    const [openState, setOpenState] = useState(false);
    const open = openProp ?? openState;
    const container = usePortalContainer();
    const change = useRef(onOpenChange);
    change.current = onOpenChange;
    const setOpen = useCallback((next) => {
        setOpenState(next);
        change.current?.(next);
    }, []);
    const close = useCallback(() => setOpen(false), [setOpen]);
    useLayer(open, close, 'popover');
    return (_jsxs(BasePopover.Root, { open: open, onOpenChange: (next) => setOpen(next), children: [_jsx(BasePopover.Trigger, { className: triggerClassName ?? 'cx-button cx-button--secondary cx-button--sm', "aria-label": triggerLabel, "data-testid": triggerTestId, disabled: disabled, children: trigger }), _jsx(BasePopover.Portal, { container: container, children: _jsx(BasePopover.Positioner, { className: "cx-positioner", sideOffset: 8, children: _jsxs(BasePopover.Popup, { className: "cx-popover", children: [_jsx(BasePopover.Title, { className: "cx-popover__title", children: title }), _jsx("div", { className: "cx-popover__body", children: children })] }) }) })] }));
}
/** A preview on hover or focus; it never takes focus and holds no controls. */
export function HoverCard({ trigger, children, delay = 300 }) {
    const container = usePortalContainer();
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    // An open card is the innermost layer, so Esc closes it before a peek
    // around it (DECISIONS entry 53 item 24). Focus that a closing layer gave
    // back to the trigger does not open it.
    useLayer(open, close, 'hover-card');
    return (_jsxs(PreviewCard.Root, { open: open, onOpenChange: (next, details) => {
            if (next && details.reason === 'trigger-focus' && hasRestoredFocus(details.trigger))
                return;
            setOpen(next);
        }, children: [_jsx(PreviewCard.Trigger, { delay: delay, closeDelay: 150, render: trigger }), _jsx(PreviewCard.Portal, { container: container, children: _jsx(PreviewCard.Positioner, { className: "cx-positioner", sideOffset: 6, align: "start", children: _jsx(PreviewCard.Popup, { className: "cx-hover-card", children: children }) }) })] }));
}
function usePeek(open, onClose, kind, trigger) {
    const ref = useRef(null);
    useLayer(open, onClose, kind, { region: ref });
    useReturnFocus(open, trigger);
    useEffect(() => {
        if (open)
            ref.current?.focus();
    }, [open]);
    return ref;
}
/** A 520 px panel that slides over the right rail. It never nests. */
export function SidePeek({ open, onClose, label, heading, actions, children, trigger }) {
    const ref = usePeek(open, onClose, 'side-peek', trigger);
    const titleId = useId();
    if (!open)
        return null;
    return (_jsxs("div", { ref: ref, className: "cx-side-peek cx-raised", role: "dialog", "aria-modal": "false", "aria-label": label, "aria-describedby": titleId, tabIndex: -1, children: [_jsxs("div", { className: "cx-peek__header", children: [_jsx("div", { className: "cx-peek__heading", id: titleId, children: heading }), _jsxs("div", { className: "cx-peek__actions", children: [actions, _jsx(IconButton, { icon: "x", label: "Close peek", onClick: onClose })] })] }), _jsx("div", { className: "cx-peek__body", children: children })] }));
}
/** A centered peek with a backdrop; focus stays inside until it closes. */
export function CenterPeek({ open, onClose, label, heading, actions, children, trigger, }) {
    const ref = usePeek(open, onClose, 'center-peek', trigger);
    const onKeyDown = useFocusTrap(ref);
    if (!open)
        return null;
    return (_jsx("div", { className: "cx-center-peek__backdrop", children: _jsxs("div", { ref: ref, className: "cx-center-peek cx-raised", role: "dialog", "aria-modal": "true", "aria-label": label, tabIndex: -1, onKeyDown: onKeyDown, children: [_jsxs("div", { className: "cx-peek__header", children: [_jsx("div", { className: "cx-peek__heading", children: heading }), _jsxs("div", { className: "cx-peek__actions", children: [actions, _jsx(IconButton, { icon: "x", label: "Close center peek", onClick: onClose })] })] }), _jsx("div", { className: "cx-peek__body", children: children })] }) }));
}
//# sourceMappingURL=overlays.js.map